<style scoped>
.filters-section {
	font-size: 0.875rem;
}

.btn-group-sm .btn {
	font-size: 0.75rem;
	padding: 0.25rem 0.5rem;
}

.form-label.small {
	margin-bottom: 0.25rem;
	color: #6c757d;
}
</style>
<template>
	<div class="card shadow-sm h-100 reports-list">
		<div class="card-header bg-light pane-header">
			<!-- Filter Section -->
			<div class="filters-section">
				<!-- Status Filter -->
				<div class="mb-2">
					<label class="form-label small fw-bold mb-1">Status</label>
					<!-- The closed bucket is a separate server query; this shows it
					     arriving without disturbing the list already on screen. -->
					<span
						v-if="closedLoading"
						class="spinner-border spinner-border-sm text-secondary ms-2"
						role="status"
						title="Loading closed reports"
					></span>
					<div class="btn-group btn-group-sm d-flex" role="group">
						<input
							type="checkbox"
							class="btn-check"
							id="status-new"
							v-model="statusFilters.new"
							autocomplete="off"
						/>
						<label class="btn btn-outline-success" for="status-new">New</label>

						<input
							type="checkbox"
							class="btn-check"
							id="status-opened"
							v-model="statusFilters.opened"
							autocomplete="off"
						/>
						<label class="btn btn-outline-primary" for="status-opened"
							>Opened</label
						>

						<input
							type="checkbox"
							class="btn-check"
							id="status-pending"
							v-model="statusFilters.pending"
							autocomplete="off"
						/>
						<label class="btn btn-outline-warning" for="status-pending"
							>Pending</label
						>

						<input
							type="checkbox"
							class="btn-check"
							id="status-closed"
							v-model="statusFilters.closed"
							autocomplete="off"
						/>
						<label class="btn btn-outline-secondary" for="status-closed"
							>Closed</label
						>
					</div>
				</div>

				<!-- Source Filter -->
				<div class="mb-2">
					<label class="form-label small fw-bold mb-1">Source</label>
					<div class="btn-group btn-group-sm d-flex" role="group">
						<button
							class="btn"
							:class="
								sourceFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'
							"
							@click="sourceFilter = 'all'"
						>
							All
						</button>
						<button
							class="btn"
							:class="
								sourceFilter === 'email'
									? 'btn-primary'
									: 'btn-outline-secondary'
							"
							@click="sourceFilter = 'email'"
						>
							<i class="bi bi-envelope"></i> Email
						</button>
						<button
							class="btn"
							:class="
								sourceFilter === 'text'
									? 'btn-primary'
									: 'btn-outline-secondary'
							"
							@click="sourceFilter = 'text'"
						>
							<i class="bi bi-chat-dots"></i> Text
						</button>
						<button
							class="btn"
							:class="
								sourceFilter === 'public_report'
									? 'btn-primary'
									: 'btn-outline-secondary'
							"
							@click="sourceFilter = 'public_report'"
						>
							<i class="bi bi-file-text"></i> Form
						</button>
					</div>
				</div>

				<!-- Type Filter: the kind of report a report communication stands
				     for is its related record's type, as the communication's own
				     type says only that it came from a report. -->
				<div class="mb-2" v-if="sourceFilter == 'public_report'">
					<label class="form-label small fw-bold mb-1">Type</label>
					<div class="btn-group btn-group-sm d-flex" role="group">
						<button
							class="btn"
							:class="
								typeFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'
							"
							@click="typeFilter = 'all'"
						>
							All
						</button>
						<button
							class="btn"
							:class="
								typeFilter === 'publicreport.compliance'
									? 'btn-success'
									: 'btn-outline-secondary'
							"
							@click="typeFilter = 'publicreport.compliance'"
						>
							<i class="bi bi-check-circle"></i> Compliance
						</button>
						<button
							class="btn"
							:class="
								typeFilter === 'publicreport.nuisance'
									? 'btn-danger'
									: 'btn-outline-secondary'
							"
							@click="typeFilter = 'publicreport.nuisance'"
						>
							<i class="bi bi-mosquito"></i> Nuisance
						</button>
						<button
							class="btn"
							:class="
								typeFilter === 'publicreport.water'
									? 'btn-info'
									: 'btn-outline-secondary'
							"
							@click="typeFilter = 'publicreport.water'"
						>
							<i class="bi bi-droplet"></i> Water
						</button>
					</div>
				</div>

				<!-- Active Filters Summary -->
				<div class="small text-muted" v-if="activeFilterCount > 0">
					<i class="bi bi-funnel"></i> {{ filteredCommunications.length }} of
					{{ props.all?.length || 0 }} reports
				</div>
			</div>
		</div>

		<div class="card-body scroll-pane">
			<div class="list-group list-group-flush">
				<div class="loading list-group-item" v-if="loading || all == null">
					Loading...
				</div>
				<div
					v-else-if="filteredCommunications.length > 0"
					v-for="comm in filteredCommunications"
					:key="comm.id"
				>
					<ListCardCommunication
						@click="handleClick(comm.id)"
						:comm="comm"
						:isSelected="selectedID == comm.id"
					/>
				</div>
				<div v-else class="list-group-item text-center text-muted p-4">
					<i class="bi bi-inbox fs-1"></i>
					<p class="mt-2">No reports match the current filters</p>
					<button class="btn btn-sm btn-outline-primary" @click="resetFilters">
						Reset Filters
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import ListCardCommunication from "@/components/ListCardCommunication.vue";
import {
	communicationMatchesStatus,
	DEFAULT_COMMUNICATION_STATUS_FILTER,
	statusFilterIsDefault,
} from "@/components/communicationFilters";
import { log } from "@/log";
import { Communication, LogEntry, PublicReport } from "@/type/api";

interface Props {
	all: Communication[] | undefined;
	loading: boolean;
	closedLoading: boolean;
	searchFilter?: string;
	selectedID?: string;
}
interface Emits {
	(e: "deselect", id: string): void;
	(e: "select", id: string): void;
	(e: "requestClosed"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const handleClick = (id: string) => {
	if (props.selectedID == undefined) {
		emit("select", id);
	} else if (props.selectedID == id) {
		emit("deselect", id);
	} else {
		emit("select", id);
	}
};

// Filter state. The source filter holds a communication's own channel — the
// API's Communication Type — and is compared against comm.type directly; the
// type filter holds the related report's type, which is where a report
// communication's kind of report lives.
type SourceFilter = "all" | "email" | "text" | "public_report";
type ReportTypeFilter =
	| "all"
	| "publicreport.compliance"
	| "publicreport.nuisance"
	| "publicreport.water";
const searchFilter = ref<string>("");
const typeFilter = ref<ReportTypeFilter>("all");
const sourceFilter = ref<SourceFilter>("all");
// The default row is the open bucket the view fetches: new, opened and
// pending ticked, closed unticked. Spreading keeps the shared default a
// constant rather than something this component mutates.
const statusFilters = ref({ ...DEFAULT_COMMUNICATION_STATUS_FILTER });

// The closed bucket is a separate server query, so the first tick asks the
// view for it. The flag stays set afterwards: unticking and re-ticking reads
// the slice the store already holds rather than asking again.
let requestedClosed = false;
watch(
	() => statusFilters.value.closed,
	(closed) => {
		if (closed && !requestedClosed) {
			requestedClosed = true;
			emit("requestClosed");
		}
	},
);

// Reset filters to default
const resetFilters = () => {
	searchFilter.value = "";
	typeFilter.value = "all";
	sourceFilter.value = "all";
	statusFilters.value = { ...DEFAULT_COMMUNICATION_STATUS_FILTER };
};

// Count active filters
const activeFilterCount = computed(() => {
	let count = 0;
	if (typeFilter.value !== "all") count++;
	if (sourceFilter.value !== "all") count++;
	if (searchFilter.value.length > 0) count++;
	if (!statusFilterIsDefault(statusFilters.value)) count++;
	return count;
});

// Filtered communications
const filteredCommunications = computed((): Communication[] => {
	log.info(
		"getting communication column list filteredCommunications",
		props.all,
	);
	if (props.all == null) {
		return [];
	}

	const filtered = props.all.filter((comm) => {
		// Status filter: the checkboxes map onto the shared buckets, so
		// "closed" covers every terminal status rather than a single enum
		// value.
		if (!communicationMatchesStatus(comm, statusFilters.value)) {
			return false;
		}

		// Source filter: the communication's own channel.
		if (sourceFilter.value !== "all" && comm.type !== sourceFilter.value) {
			return false;
		}

		// Type filter: the kind of report a report communication stands for is
		// its related record's type, since the communication's own type says
		// only that it came from a report.
		if (
			sourceFilter.value === "public_report" &&
			typeFilter.value !== "all" &&
			!comm.related.some((r) => r.type === typeFilter.value)
		) {
			return false;
		}

		return true;
	});
	// Filtering preserves the order the API returned. The server orders both
	// buckets by when the row last changed status, not by when it arrived, so
	// a communication closed today is not buried under ones closed last year.
	log.info("filtered to", filtered);
	return filtered;
});
</script>
